import { useEffect, useMemo, useRef, useState } from "react";
import { buildCalendarMonth } from "@tc/shared";
import { formatDate, type Locale } from "@tc/i18n";
import { Icon } from "./icon";

export interface DateRange {
  /** Inclusive start, ISO `YYYY-MM-DD`, or null when unset. */
  start: string | null;
  /** Inclusive end, ISO `YYYY-MM-DD`, or null when only the start is chosen. */
  end: string | null;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Active UI locale — month/weekday names and the trigger label are localized in it. */
  locale: Locale;
  placeholder?: string;
  /** Earliest selectable day (ISO `YYYY-MM-DD`); earlier days render disabled. */
  min?: string;
  /** When set, shows a footer button with this label that clears the range. */
  clearLabel?: string;
}

const DAY_FMT: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };

/**
 * A two-click date-range picker in a popover (TW user-testing): click a start day, then an end day.
 * The day grid reuses the shared `buildCalendarMonth` fold (Monday-first, tz-safe `YYYY-MM-DD`
 * strings — compared lexicographically, never parsed) so it matches the app calendar's bucketing.
 * The stored value is always `{ start, end }` ISO strings; `end` is guaranteed ≥ `start`.
 */
export function DateRangePicker({ value, onChange, locale, placeholder, min, clearLabel }: DateRangePickerProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  const todayIso = useMemo(() => new Date().toISOString(), []);
  const anchor = value.start ?? value.end ?? todayIso.slice(0, 10);
  const [view, setView] = useState(() => {
    const parts = anchor.split("-");
    return { year: Number(parts[0]) || new Date().getFullYear(), month: (Number(parts[1]) || 1) - 1 };
  });

  // Close on an outside click while the popover is open.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const grid = useMemo(() => buildCalendarMonth(view.year, view.month, [], todayIso), [view, todayIso]);
  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => formatDate(locale, new Date(2024, 0, 1 + i), { weekday: "narrow" })),
    [locale],
  );

  const fmt = (d: string) => formatDate(locale, d, DAY_FMT);
  const label =
    value.start && value.end
      ? `${fmt(value.start)} – ${fmt(value.end)}`
      : value.start
        ? `${fmt(value.start)} – …`
        : null;

  function pick(date: string) {
    if (min && date < min) return;
    const { start, end } = value;
    if (!start || end) {
      onChange({ start: date, end: null }); // begin (or restart) the range
      return;
    }
    if (date < start) {
      onChange({ start: date, end: null }); // earlier than the start → make it the new start
      return;
    }
    onChange({ start, end: date }); // complete the range
    setOpen(false);
  }

  // The end-preview: while choosing the end, shade from start to the hovered day.
  const previewEnd = !value.end && value.start && hovered && hovered >= value.start ? hovered : value.end;
  const inRange = (date: string) =>
    value.start && previewEnd && date > value.start && date < previewEnd;

  return (
    <div className="drp" ref={wrapRef}>
      <button
        type="button"
        className={`input drp-trigger${label ? "" : " is-placeholder"}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{label ?? placeholder ?? ""}</span>
        <Icon name="calendar" size={16} className="drp-trigger-ic" />
      </button>

      {open && (
        <div className="drp-pop" role="dialog">
          <div className="drp-head">
            <button type="button" className="drp-nav" aria-label="previous month" onClick={() => setView(shift(view, -1))}>
              <Icon name="chevL" size={16} />
            </button>
            <b className="drp-month">{formatDate(locale, new Date(view.year, view.month, 1), { month: "long", year: "numeric" })}</b>
            <button type="button" className="drp-nav" aria-label="next month" onClick={() => setView(shift(view, 1))}>
              <Icon name="chevR" size={16} />
            </button>
          </div>
          <div className="drp-grid">
            {weekdays.map((d, i) => (
              <div key={`w${i}`} className="drp-dow">
                {d}
              </div>
            ))}
            {grid.cells.map((cell, i) => {
              if (cell.day === null || cell.date === null) return <div key={i} className="drp-cell is-pad" />;
              const date = cell.date;
              const disabled = Boolean(min && date < min);
              const isStart = date === value.start;
              const isEnd = date === value.end;
              const cls = [
                "drp-cell",
                cell.isToday ? "is-today" : "",
                disabled ? "is-disabled" : "",
                isStart || isEnd ? "is-sel" : "",
                inRange(date) ? "in-range" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  key={i}
                  type="button"
                  className={cls}
                  disabled={disabled}
                  onMouseEnter={() => setHovered(date)}
                  onClick={() => pick(date)}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
          {clearLabel && (
            <div className="drp-foot">
              <button type="button" className="link" onClick={() => onChange({ start: null, end: null })}>
                {clearLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Move the {year, month} view by `delta` months, rolling the year over. */
function shift(view: { year: number; month: number }, delta: number): { year: number; month: number } {
  const m = view.month + delta;
  return { year: view.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
}
